import { useCallback, useMemo } from "react";
import {
  ColDef,
  GetContextMenuItemsParams,
  MenuItemDef,
} from "ag-grid-community";
import { useAppContext } from "@/context/context";
import ActiveStateCell from "@/components/AGCellRenderers/ActiveStateCell";
import UserRolesCell from "@/components/AGCellRenderers/UserRolesCell";
import UserPortfoliosCell from "@/components/AGCellRenderers/UserPortfoliosCell";
import { IAGRoleManagementDataRow } from "@pulse-ui/types/components/AGTable";
import { IUser } from "@pulse-ui/types/components/RoleManagement";

interface UseRoleManagementTableProps {
  onViewDetails: (user: IUser) => void;
  onDeleteUser: (userId: string, userName: string) => void;
}

export const useRoleManagementTable = ({
  onViewDetails,
  onDeleteUser,
}: UseRoleManagementTableProps) => {
  const { context_state } = useAppContext();

  const colDefs = useMemo<ColDef<IAGRoleManagementDataRow>[]>(
    () => [
      {
        headerName: "First Name",
        field: "firstname",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Last Name",
        field: "lastname",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Status",
        field: "status",
        cellClass: "ag-center-aligned-cell",
        cellRenderer: ActiveStateCell,
        cellRendererParams: (params: any) => ({
          active_state: params.value,
        }),
      },
      {
        headerName: "Roles",
        field: "roles",
        cellClass: "ag-center-aligned-cell",
        cellRenderer: UserRolesCell,
        cellRendererParams: (params: any) => ({
          roles: params.value,
        }),
      },
      {
        headerName: "Portfolios",
        field: "portfolios",
        cellClass: "ag-center-aligned-cell",
        cellRenderer: UserPortfoliosCell,
        cellRendererParams: (params: any) => ({
          portfolios: params.value,
          roles: params.data.roles,
          portfolios_context_data: context_state.org_metadata?.portfolios,
        }),
      },
      {
        headerName: "Created",
        field: "created",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Last Modified",
        field: "last_modified",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [context_state.org_metadata?.portfolios],
  );

  const defaultColDef = useMemo<ColDef>(() => ({ flex: 1 }), []);

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): MenuItemDef[] => {
      const user: IUser = params?.node?.data?.original_row;
      const firstname: string = params?.node?.data?.firstname ?? "";
      const lastname: string = params?.node?.data?.lastname ?? "";

      return [
        {
          name: "View Details",
          action: () => onViewDetails(user),
          cssClasses: ["pulse-color--background-100"],
        },
        {
          name: "Delete User",
          action: () =>
            onDeleteUser(user.org_user_id, `${firstname} ${lastname}`),
          cssClasses: ["pulse-color--error-400"],
        },
      ];
    },
    [onViewDetails, onDeleteUser],
  );

  return { colDefs, defaultColDef, getContextMenuItems };
};
